import axios from 'axios'

/**
 * Wire-shape mirror of backend `previewPhase2Migration` response.
 * Source of truth: apps/services/main-service/src/organizations/phase2-migration.service.ts
 *
 * Keep these types in sync with the backend exactly. The backend wins; if it changes,
 * update here. Do NOT add UI-only convenience fields to these types — derive in the
 * component instead so we never mistake a frontend computation for a backend value.
 */

/**
 * Subset of `Company` we actually use in the migration UI. The backend serializes the
 * full `Company` shape — extra fields are simply ignored here. Only fields we render
 * are listed for clarity.
 */
export interface Phase2SampleCompany {
    name: string
    mcNumber: string
    phoneNumber: string
    dotNumber?: string
    details?: string
    companyAddress?: string
    transferNumber?: string
    aiAgentName?: string
}

export interface Phase2McGroup {
    mc: string
    emails: string[]
    sampleCompany: Phase2SampleCompany
}

export interface Phase2Divergence {
    mcNumber: string
    values: Array<{ value: string; emails: string[] }>
}

export interface Phase2PreviewApp {
    appId: string
    projectName: string
    anchorMc: string | null
    mcGroups: Phase2McGroup[]
    aiAgentDivergences?: Phase2Divergence[]
    transferNumberDivergences?: Phase2Divergence[]
    expectedSplits: number
}

export type Phase2WarningType = 'aiAgentDivergence' | 'transferNumberDivergence'

export interface Phase2Warning {
    id: string
    type: Phase2WarningType
    appId: string
    mc: string
    description: string
}

/** Mirror of the backend `ClientOrganization` shape we actually need on this screen. */
export interface Phase2Organization {
    id: string
    name?: string
    ownerId: string
    applicationIds: string[]
    multiMcEnabled: boolean
    companyFromParentEnabled: boolean
}

export interface Phase2PreviewResponse {
    organization: Phase2Organization
    apps: Phase2PreviewApp[]
    warnings: Phase2Warning[]
    alreadyMigrated: boolean
}

export interface Phase2ExecuteResult {
    newApplicationIds: string[]
    flagEnabled: true
    processedApps: number
    splitsCreated: number
}

interface Phase2DisableResult {
    flagEnabled: false
}

interface ApiEnvelope<T> {
    success: boolean
    data: T
    message?: string
}

const PHASE2_BASE = '/gluon/organizations/phase2'

export const phase2MigrationApi = {
    async getPreview(ownerEmail: string): Promise<Phase2PreviewResponse> {
        const res = await axios.get<ApiEnvelope<Phase2PreviewResponse>>(`${PHASE2_BASE}/preview`, {
            params: { email: ownerEmail },
        })
        return res.data.data
    },

    async executeMigration(ownerEmail: string, acknowledgedWarnings: Record<string, string>): Promise<Phase2ExecuteResult> {
        const res = await axios.post<ApiEnvelope<Phase2ExecuteResult>>(`${PHASE2_BASE}/execute`, {
            ownerEmail,
            acknowledgedWarnings,
        })
        return res.data.data
    },

    async disableFlag(ownerEmail: string, reason: string): Promise<Phase2DisableResult> {
        const res = await axios.post<ApiEnvelope<Phase2DisableResult>>(`${PHASE2_BASE}/disable-flag`, {
            ownerEmail,
            reason,
        })
        return res.data.data
    },
}

/**
 * Compile-time wire-shape assertion: a fixture matching the backend response must
 * satisfy `Phase2PreviewResponse`. If the backend shape diverges, this block fails
 * `tsc --noEmit` and the build catches the regression before runtime.
 *
 * Kept in this file (and not a separate test) because gluon has no test runner.
 */
const _phase2PreviewWireShapeFixture: Phase2PreviewResponse = {
    organization: {
        id: 'org_1',
        ownerId: 'user_1',
        applicationIds: ['app_1'],
        multiMcEnabled: false,
        companyFromParentEnabled: false,
    },
    apps: [
        {
            appId: 'app_1',
            projectName: 'acme',
            anchorMc: '111',
            mcGroups: [
                {
                    mc: '111',
                    emails: ['a@x.com'],
                    sampleCompany: { name: 'Acme', mcNumber: '111', phoneNumber: '+1' },
                },
                {
                    mc: '222',
                    emails: ['b@x.com'],
                    sampleCompany: { name: 'Acme 222', mcNumber: '222', phoneNumber: '+1' },
                },
            ],
            aiAgentDivergences: [{ mcNumber: '111', values: [{ value: 'Bot A', emails: ['a@x.com'] }] }],
            transferNumberDivergences: [],
            expectedSplits: 1,
        },
    ],
    warnings: [
        {
            id: 'aiAgentDivergence:app_1:111',
            type: 'aiAgentDivergence',
            appId: 'app_1',
            mc: '111',
            description: 'Multiple aiAgentName values for MC 111 on App acme',
        },
    ],
    alreadyMigrated: false,
}
// Suppress unused-binding warning while keeping the assertion alive for tsc.
void _phase2PreviewWireShapeFixture
